import { OnlineOrderDeliveryMode } from '@app/graphql/types/graphql';
import useOnlineOrderServiceAvailable from '@app/utils/use-online-order-service-available';
import { useTranslate } from '@tolgee/react';
import classNames from 'classnames';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

interface OnOffButtonProps {
  branchId: string;
}

const serviceOptions: OnlineOrderDeliveryMode[] = [OnlineOrderDeliveryMode.Delivery, OnlineOrderDeliveryMode.Pickup];

export default function SwitchModeButton(props: OnOffButtonProps) {
  const [params] = useSearchParams();
  const initialDeliveryModeString = params.get('initialDeliveryMode');

  const { data: serviceAvailableData } = useOnlineOrderServiceAvailable(props.branchId);

  const [mode, setMode] = useLocalStorage<OnlineOrderDeliveryMode | null>('deliveryMode', null);
  const { t } = useTranslate();

  const filterServiceOptions = serviceOptions.filter((item) => {
    if (
      item == OnlineOrderDeliveryMode.Delivery &&
      serviceAvailableData?.storefront_isEachOnlineOrderServiceAvailable.deliveryAvailable
    ) {
      return true;
    }

    if (
      item == OnlineOrderDeliveryMode.Pickup &&
      serviceAvailableData?.storefront_isEachOnlineOrderServiceAvailable.pickupAvailable
    ) {
      return true;
    }

    return false;
  });

  useEffect(() => {
    const deliveryMode = mode ?? convertDeliveryModeStringToEnum(initialDeliveryModeString);
    if (filterServiceOptions.includes(deliveryMode)) {
      setMode(deliveryMode);
    } else {
      setMode(filterServiceOptions[0]);
    }
  }, [filterServiceOptions, initialDeliveryModeString, mode, setMode]);

  if (!serviceAvailableData) {
    return;
  }

  const visibleButtonCount = filterServiceOptions.length;
  const buttonWidthClass = visibleButtonCount === 1 ? 'basis-full' : 'basis-1/2';

  return (
    <div className="toggle-btn">
      <p className="toggle-btn__label">
        {t('online-order.delivery-selection', 'Delivery selection')}
      </p>
      <div className="toggle-btn__switch">
        {filterServiceOptions.includes(OnlineOrderDeliveryMode.Pickup) && (
          <button
            type="button"
            onClick={() => {
              setMode(OnlineOrderDeliveryMode.Pickup);
            }}
            disabled={!filterServiceOptions.includes(OnlineOrderDeliveryMode.Pickup)}
            className={classNames(
              'toggle-btn__option toggle-btn__option--pickup',
              buttonWidthClass,
              mode == OnlineOrderDeliveryMode.Pickup && 'toggle-btn__option--active',
              !filterServiceOptions.includes(OnlineOrderDeliveryMode.Pickup) && 'toggle-btn__option--disabled'
            )}
          >
            {t('common.pickup', 'Pickup')}
          </button>
        )}
        {filterServiceOptions.includes(OnlineOrderDeliveryMode.Delivery) && (
          <button
            type="button"
            onClick={() => {
              setMode(OnlineOrderDeliveryMode.Delivery);
            }}
            disabled={!filterServiceOptions.includes(OnlineOrderDeliveryMode.Delivery)}
            className={classNames(
              'toggle-btn__option toggle-btn__option--delivery',
              buttonWidthClass,
              mode == OnlineOrderDeliveryMode.Delivery && 'toggle-btn__option--active',
              !filterServiceOptions.includes(OnlineOrderDeliveryMode.Delivery) && 'toggle-btn__option--disabled'
            )}
          >
            {t('common.delivery', 'Delivery')}
          </button>
        )}
      </div>
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function convertDeliveryModeStringToEnum(deliveryModeString: string | null | undefined) {
  const formatString = deliveryModeString?.toLowerCase().trim();

  if (formatString) {
    if (formatString.includes('pickup')) {
      return OnlineOrderDeliveryMode.Pickup;
    }
    if (formatString.includes('delivery')) {
      return OnlineOrderDeliveryMode.Delivery;
    }
  }

  return OnlineOrderDeliveryMode.Pickup;
}
